import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type {
  ArtikelMatchPosition,
  ArtikelTreffer,
  Schnellkalkulation,
} from "@bidi-handel/core/schemas";
import { Icon } from "../../shell/Icon";
import type { PositionWithKonsol } from "./oz-tree";

/**
 * Side-Drawer rechts für eine Match-Position. Drei Ansichten in Schichten:
 *
 *  1. Drawer-Hauptansicht
 *     - Header: Position-Info + Aktions-Zeile (Manuell wählen / Schließen)
 *     - Body: Top-Pick als volle Karte + "X weitere Vorschläge"-Banner
 *
 *  2. List-Modal (subView = "list")
 *     - Geöffnet via "X weitere Vorschläge"-Banner
 *     - Liste der Nicht-Top-Kandidaten mit Such-/Filter-Box
 *     - Click auf Row → Detail-Modal (from="list")
 *
 *  3. Detail-Modal (subView = { kind: "detail", code, from })
 *     - Geöffnet via Top-Pick-Details-Button oder Click auf List-Row
 *     - Original-Anforderung vs. Match-Begründung gegenübergestellt
 *     - "Übernehmen", "Schließen", optional "Zurück zur Liste" wenn from=="list"
 */

const STATUS_LABEL: Record<string, string> = {
  auto_uebernehmen: "PICK",
  vorschlagen: "PRÜFEN",
  pruefen: "PRÜFEN",
  nicht_geeignet: "NICHT GEEIGNET",
};

const STATUS_TONE: Record<string, "ok" | "warn" | ""> = {
  auto_uebernehmen: "ok",
  vorschlagen: "warn",
  pruefen: "warn",
  nicht_geeignet: "",
};

function scoreTone(score: number): "s-ok" | "s-warn" | "s-bad" {
  if (score >= 80) return "s-ok";
  if (score >= 40) return "s-warn";
  return "s-bad";
}

type SubView =
  | null
  | { kind: "list" }
  | { kind: "detail"; code: string; from: "list" | "drawer" };

export function MatchDrawer({
  position,
  pending,
  onPick,
  onSetNichtAngeboten,
  onSetSchnellkalkulation,
  onOpenPicker,
  onOpenKomponenten,
  onClose,
}: {
  position: PositionWithKonsol;
  pending: string | null;
  onPick: (positionId: string, artikelCode: string | null) => Promise<void>;
  onSetNichtAngeboten: (positionId: string, value: boolean) => Promise<void>;
  onSetSchnellkalkulation: (
    positionId: string,
    schnellkalkulation: Schnellkalkulation | null
  ) => Promise<void>;
  onOpenPicker: (p: PositionWithKonsol) => void;
  onOpenKomponenten: (p: PositionWithKonsol) => void;
  onClose: () => void;
}) {
  const { match, konsol } = position;
  const isPending = pending === match.positionId;
  const picked = match.gewaehlterArtikel;
  const nichtAngeboten = match.nichtAngeboten;
  const schnellkalk = match.schnellkalkulation;
  const noKandidaten = match.matchKandidaten.length === 0;

  const allSorted = useMemo<ArtikelTreffer[]>(
    () => [...match.matchKandidaten].sort((a, b) => b.matchScore - a.matchScore),
    [match.matchKandidaten]
  );
  const topPick = allSorted[0] ?? null;
  const restCandidates = allSorted.slice(1);

  const [subView, setSubView] = useState<SubView>(null);

  const detailCandidate = useMemo<ArtikelTreffer | null>(() => {
    if (subView?.kind !== "detail") return null;
    return allSorted.find((k) => k.artikelCode === subView.code) ?? null;
  }, [subView, allSorted]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Esc auf gestapelten Modals schließt erst die oberste Schicht.
      if (subView) setSubView(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, subView]);

  function pick(code: string | null) {
    onPick(match.positionId, code).catch(() => {});
  }
  function toggleNichtAngeboten() {
    onSetNichtAngeboten(match.positionId, !nichtAngeboten).catch(() => {});
  }
  function removeSchnellkalk() {
    onSetSchnellkalkulation(match.positionId, null).catch(() => {});
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label="Match-Kandidaten">
        <div className="drawer-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="uppercase-mini">/ MATCH / POSITION {match.oz}</div>
            <div className="drawer-title">
              {konsol?.originaltext ?? "(LV-Originaltext nicht verfügbar)"}
            </div>
            <div className="drawer-meta">
              {konsol && (
                <span className="bv-chip mono">
                  {konsol.menge} {konsol.einheit}
                </span>
              )}
              <span className="id-chip">{match.positionId}</span>
              {nichtAngeboten ? (
                <span className="pill excluded">
                  <span className="dot" />
                  Nicht angeboten
                </span>
              ) : schnellkalk ? (
                <span className={`pill ${schnellkalk.closed ? "ok" : "warn"}`}>
                  <span className="dot" />
                  Schnellkalk {schnellkalk.closed ? "· abgeschlossen" : "· in Arbeit"} · {schnellkalk.zeilen.length} Zeile
                  {schnellkalk.zeilen.length === 1 ? "" : "n"}
                </span>
              ) : (
                <span className={`pill ${picked ? "ok" : "warn"}`}>
                  <span className="dot" />
                  {picked ? `Gewählt: ${picked}` : "Kein Pick"}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="drawer-actions">
          <button
            type="button"
            className="btn"
            onClick={() => onOpenPicker(position)}
            disabled={isPending || nichtAngeboten}
            title={
              nichtAngeboten
                ? "Position als Nicht-angeboten markiert — erst wieder aufnehmen"
                : undefined
            }
          >
            <Icon name="search" size={14} /> Anderen Artikel manuell wählen …
          </button>
          <button
            type="button"
            className={`btn ${match.serviceErkannt && !schnellkalk ? "primary" : ""}`}
            onClick={() => onOpenKomponenten(position)}
            disabled={isPending || nichtAngeboten}
            title={
              nichtAngeboten
                ? "Position als Nicht-angeboten markiert — erst wieder aufnehmen"
                : "Mehrere Komponenten zu einem Einheitspreis zusammenstellen (Lohn + Material + Sonstiges)"
            }
          >
            🔧 {schnellkalk ? "Schnellkalk bearbeiten" : "Schnellkalkulation"}
          </button>
          <button
            type="button"
            className={`btn ${nichtAngeboten ? "primary" : "danger"}`}
            onClick={toggleNichtAngeboten}
            disabled={isPending}
          >
            {nichtAngeboten ? "↺ Wieder anbieten" : "✕ Nicht anbieten"}
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn ghost" onClick={onClose}>
            Schliessen
          </button>
        </div>

        <div className="drawer-body">
          {nichtAngeboten ? (
            <div
              className="warn-line"
              style={{
                borderLeftColor: "var(--fg-faint)",
                background: "var(--bg-base)",
              }}
            >
              <strong>Diese Position wird nicht angeboten.</strong>{" "}
              Sie ist aus dem Angebot ausgeschlossen — keine Bepreisung, kein
              Eintrag im Angebots-Export. Über „Wieder anbieten" oben kannst du
              die Auswahl zurücknehmen.
            </div>
          ) : schnellkalk ? (
            <SchnellkalkSummaryCard
              schnellkalk={schnellkalk}
              onEdit={() => onOpenKomponenten(position)}
              onRemove={removeSchnellkalk}
              isPending={isPending}
            />
          ) : noKandidaten ? (
            <div
              className="warn-line"
              style={{ borderLeftColor: "var(--warn)" }}
            >
              <strong>Keine automatischen Treffer aus den Stammdaten.</strong>{" "}
              Manuell aus dem Stammdaten-Pool zuordnen (Button oben).
            </div>
          ) : (
            <>
              {match.serviceErkannt && (
                <div className="service-banner" style={{ marginBottom: 14 }}>
                  <div className="service-banner-head">
                    <span className="service-banner-icon">⚠</span>
                    <strong>Dienstleistung erkannt</strong>
                    <span className="cc-score-pill s-warn">
                      Score gecappt bei {match.serviceErkannt.capApplied}
                    </span>
                  </div>
                  <div className="service-banner-body">
                    LV-Text enthält Verben wie{" "}
                    <span className="mono">
                      {[
                        ...match.serviceErkannt.verben,
                        ...match.serviceErkannt.phrasen,
                      ]
                        .slice(0, 5)
                        .join(", ")}
                    </span>
                    . Stammdaten-Match deckt nur Material — Service-Teil
                    als <strong>Schnellkalkulation</strong> aufschlüsseln,
                    Material allein picken, oder Position ausschließen.
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn sm primary"
                      onClick={() => onOpenKomponenten(position)}
                      disabled={isPending}
                    >
                      🔧 Schnellkalkulation konfigurieren
                    </button>
                  </div>
                </div>
              )}
              {topPick && (
                <TopPickCard
                  k={topPick}
                  isPicked={topPick.artikelCode === picked}
                  isPending={isPending}
                  onPick={pick}
                  onDetails={() =>
                    setSubView({
                      kind: "detail",
                      code: topPick.artikelCode,
                      from: "drawer",
                    })
                  }
                />
              )}

              {restCandidates.length > 0 && (
                <button
                  type="button"
                  className="alt-banner"
                  onClick={() => setSubView({ kind: "list" })}
                >
                  <span className="alt-banner-icon">
                    <Icon name="chev" size={16} />
                  </span>
                  <span className="alt-banner-text">
                    <strong>
                      {restCandidates.length} weitere Vorschlag
                      {restCandidates.length === 1 ? "" : "e"}
                    </strong>{" "}
                    <span className="dim">
                      vom Match-Agent · click zum Ansehen
                    </span>
                  </span>
                  <span className="alt-banner-scores">
                    {restCandidates.slice(0, 5).map((k) => (
                      <span
                        key={k.artikelCode}
                        className={`cc-score-pill ${scoreTone(k.matchScore)}`}
                      >
                        {k.matchScore}
                      </span>
                    ))}
                    {restCandidates.length > 5 && (
                      <span className="dim mono">
                        +{restCandidates.length - 5}
                      </span>
                    )}
                  </span>
                </button>
              )}
            </>
          )}
        </div>
      </aside>

      {subView?.kind === "list" && (
        <AlternativesListModal
          positionOz={match.oz}
          positionTitle={
            konsol?.originaltext ?? "(LV-Originaltext nicht verfügbar)"
          }
          candidates={restCandidates}
          picked={picked}
          isPending={isPending}
          onPick={pick}
          onDetail={(code) =>
            setSubView({ kind: "detail", code, from: "list" })
          }
          onClose={() => setSubView(null)}
        />
      )}

      {subView?.kind === "detail" && detailCandidate && (
        <CandidateDetailModal
          k={detailCandidate}
          isPicked={detailCandidate.artikelCode === picked}
          isPending={isPending}
          positionOz={match.oz}
          originaltext={
            konsol?.originaltext ?? "(LV-Originaltext nicht verfügbar)"
          }
          konsolMenge={konsol?.menge}
          konsolEinheit={konsol?.einheit}
          serviceErkannt={match.serviceErkannt}
          onPick={(code) => {
            pick(code);
            setSubView(null);
          }}
          onBack={
            subView.from === "list"
              ? () => setSubView({ kind: "list" })
              : null
          }
          onClose={() => setSubView(null)}
        />
      )}
    </>
  );
}

/* ------------------------ Sub-Komponenten ------------------------ */

function TopPickCard({
  k,
  isPicked,
  isPending,
  onPick,
  onDetails,
}: {
  k: ArtikelTreffer;
  isPicked: boolean;
  isPending: boolean;
  onPick: (code: string | null) => void;
  onDetails: () => void;
}) {
  const tone = STATUS_TONE[k.status] ?? "";
  return (
    <div
      className={`candidate ${
        isPicked
          ? "cand-pick"
          : k.status === "pruefen" || k.status === "vorschlagen"
            ? "cand-pruefen"
            : ""
      }`}
    >
      <div>
        <div className="cand-head">
          <span className="cand-sku mono">{k.artikelCode}</span>
          <span className={`pill ${isPicked ? "ok" : tone}`}>
            <span className="dot" />
            {isPicked ? "AKTIV" : STATUS_LABEL[k.status] ?? k.status}
          </span>
          <span
            className="uppercase-mini"
            style={{ marginLeft: "auto", color: "var(--fg-faint)" }}
          >
            TOP-VORSCHLAG
          </span>
        </div>
        <div className="cand-reason">{k.begruendung}</div>
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            className="btn ghost sm"
            onClick={onDetails}
          >
            <Icon name="search" size={12} /> Detail-Bericht öffnen
          </button>
        </div>
      </div>
      <div className="cand-r">
        <div className="cand-score">
          <svg viewBox="0 0 40 40" width="40" height="40">
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke="var(--line)"
              strokeWidth="3"
              fill="none"
            />
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke={
                k.matchScore >= 80
                  ? "var(--ok)"
                  : k.matchScore >= 40
                    ? "var(--warn)"
                    : "var(--bad)"
              }
              strokeWidth="3"
              fill="none"
              strokeDasharray={`${(k.matchScore / 100) * 100.5} 100.5`}
              transform="rotate(-90 20 20)"
              strokeLinecap="round"
            />
          </svg>
          <span className="cand-score-num mono">{k.matchScore}</span>
        </div>
        {isPicked ? (
          <button
            type="button"
            className="btn ghost sm"
            disabled={isPending}
            onClick={() => onPick(null)}
          >
            {isPending ? "…" : "entfernen"}
          </button>
        ) : (
          <button
            type="button"
            className="btn sm primary"
            disabled={isPending}
            onClick={() => onPick(k.artikelCode)}
          >
            {isPending ? "…" : "Übernehmen"}
          </button>
        )}
      </div>
    </div>
  );
}

function AlternativesListModal({
  positionOz,
  positionTitle,
  candidates,
  picked,
  isPending,
  onPick,
  onDetail,
  onClose,
}: {
  positionOz: string;
  positionTitle: string;
  candidates: ArtikelTreffer[];
  picked: string | null;
  isPending: boolean;
  onPick: (code: string | null) => void;
  onDetail: (code: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (k) =>
        k.artikelCode.toLowerCase().includes(q) ||
        k.begruendung.toLowerCase().includes(q)
    );
  }, [candidates, query]);

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 880 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Alle Vorschläge"
      >
        <div className="modal-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="uppercase-mini">
              / VORSCHLÄGE / POSITION {positionOz}
            </div>
            <div
              className="modal-title"
              style={{ fontSize: 18, marginTop: 6 }}
            >
              {positionTitle}
            </div>
            <div className="uppercase-mini" style={{ marginTop: 8 }}>
              {candidates.length} Alternativen vom Match-Agent
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="drawer-search" style={{ borderRadius: 0 }}>
          <Icon name="search" size={14} />
          <input
            type="search"
            placeholder="Suchen in Vorschlägen (Code, Begründung) …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Vorschläge durchsuchen"
          />
          <span className="drawer-search-count mono">
            {filtered.length} / {candidates.length}
          </span>
        </div>

        <div className="modal-body" style={{ paddingTop: 14 }}>
          {filtered.length === 0 ? (
            <div className="candidate-empty">
              Keine Treffer für „{query}" in den Vorschlägen.
            </div>
          ) : (
            <div className="alt-list">
              {filtered.map((k) => (
                <CandidateRow
                  key={k.artikelCode}
                  k={k}
                  isPicked={k.artikelCode === picked}
                  isPending={isPending}
                  onDetail={() => onDetail(k.artikelCode)}
                  onPick={() => onPick(k.artikelCode)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function CandidateRow({
  k,
  isPicked,
  isPending,
  onDetail,
  onPick,
}: {
  k: ArtikelTreffer;
  isPicked: boolean;
  isPending: boolean;
  onDetail: () => void;
  onPick: () => void;
}) {
  const tone = STATUS_TONE[k.status] ?? "";
  const scoreCls = scoreTone(k.matchScore);

  function handleRowClick(e: React.MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement;
    if (target.closest("button[data-row-action]")) return;
    onDetail();
  }
  function handleKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onDetail();
    }
  }

  return (
    <div
      className={`candidate-compact ${isPicked ? "is-picked" : ""}`}
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={handleKey}
      aria-label={`Vorschlag ${k.artikelCode} öffnen`}
    >
      <span className="cc-sku mono">{k.artikelCode}</span>
      <span className="cc-reason">{k.begruendung}</span>
      <span className={`pill cc-pill ${isPicked ? "ok" : tone}`}>
        <span className="dot" />
        {isPicked ? "AKTIV" : STATUS_LABEL[k.status] ?? k.status}
      </span>
      <span className={`cc-score-pill ${scoreCls}`}>{k.matchScore}</span>
      <span className="cc-actions">
        <button
          type="button"
          data-row-action
          className="btn ghost sm"
          disabled={isPending}
          onClick={(e) => {
            e.stopPropagation();
            onPick();
          }}
          aria-label={`Vorschlag ${k.artikelCode} übernehmen`}
        >
          {isPending ? "…" : "Übernehmen"}
        </button>
      </span>
    </div>
  );
}

function CandidateDetailModal({
  k,
  isPicked,
  isPending,
  positionOz,
  originaltext,
  konsolMenge,
  konsolEinheit,
  serviceErkannt,
  onPick,
  onBack,
  onClose,
}: {
  k: ArtikelTreffer;
  isPicked: boolean;
  isPending: boolean;
  positionOz: string;
  originaltext: string;
  konsolMenge?: number;
  konsolEinheit?: string;
  serviceErkannt: {
    verben: string[];
    phrasen: string[];
    capApplied: number;
  } | null;
  onPick: (code: string | null) => void;
  onBack: null | (() => void);
  onClose: () => void;
}) {
  const tone = STATUS_TONE[k.status] ?? "";
  const scoreCls = scoreTone(k.matchScore);

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 760 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Vorschlag-Details"
      >
        <div className="modal-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="uppercase-mini">
              / DETAIL / POSITION {positionOz}
            </div>
            <div className="detail-head-row">
              <span className="cand-sku mono" style={{ fontSize: 16 }}>
                {k.artikelCode}
              </span>
              <span className={`pill ${isPicked ? "ok" : tone}`}>
                <span className="dot" />
                {isPicked ? "AKTIV" : STATUS_LABEL[k.status] ?? k.status}
              </span>
              <span className={`cc-score-pill ${scoreCls}`}>
                Score {k.matchScore}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {serviceErkannt && (
            <div className="service-banner">
              <div className="service-banner-head">
                <span className="service-banner-icon">⚠</span>
                <strong>Dienstleistung im LV-Text erkannt</strong>
                <span className="cc-score-pill s-warn">
                  Score gecappt bei {serviceErkannt.capApplied}
                </span>
              </div>
              <div className="service-banner-body">
                Der LV-Text enthält Service-Verben{" "}
                {serviceErkannt.verben.length > 0 && (
                  <>
                    (<span className="mono">{serviceErkannt.verben.join(", ")}</span>
                    {serviceErkannt.phrasen.length > 0 && ", "}
                  </>
                )}
                {serviceErkannt.phrasen.length > 0 && (
                  <>
                    <span className="mono">{serviceErkannt.phrasen.join(", ")}</span>
                    {serviceErkannt.verben.length > 0 ? ")" : ""}
                  </>
                )}
                {serviceErkannt.verben.length > 0 &&
                  serviceErkannt.phrasen.length === 0 &&
                  ")"}
                . Unsere Stammdaten decken nur Material ab — Transport, Verlegung
                oder Montage müssen separat als Lohn bepreist oder die Position
                via „Nicht anbieten" ausgeschlossen werden.
              </div>
            </div>
          )}
          <div className="detail-section">
            <div className="detail-label uppercase-mini">
              Original-Anforderung (LV-Text)
            </div>
            <div className="detail-original">{originaltext}</div>
            {konsolMenge != null && konsolEinheit && (
              <div className="detail-meta">
                <span className="bv-chip mono">
                  {konsolMenge} {konsolEinheit}
                </span>
              </div>
            )}
          </div>

          <div className="detail-divider" />

          <div className="detail-section">
            <div className="detail-label uppercase-mini">
              Bewertung des Match-Agents
            </div>
            <div className="detail-reason">{k.begruendung}</div>
          </div>

          <div className="detail-divider" />

          <div className="detail-section">
            <div className="detail-label uppercase-mini">
              Score-Einordnung
            </div>
            <div className="detail-scores">
              <ScoreBand
                label="≥ 80"
                cls="s-ok"
                desc="Auto-Übernehmen — passt sehr gut"
                active={k.matchScore >= 80}
              />
              <ScoreBand
                label="60–79"
                cls="s-warn"
                desc="Vorschlagen — prüfen empfohlen"
                active={k.matchScore >= 60 && k.matchScore < 80}
              />
              <ScoreBand
                label="40–59"
                cls="s-warn"
                desc="Prüfen — Diskrepanz vorhanden"
                active={k.matchScore >= 40 && k.matchScore < 60}
              />
              <ScoreBand
                label="< 40"
                cls="s-bad"
                desc="Nicht geeignet — Falscher Kontext"
                active={k.matchScore < 40}
              />
            </div>
          </div>
        </div>

        <div className="modal-foot">
          {onBack && (
            <button type="button" className="btn ghost" onClick={onBack}>
              ← Zurück zur Liste
            </button>
          )}
          <div style={{ flex: 1 }} />
          {isPicked ? (
            <button
              type="button"
              className="btn ghost"
              disabled={isPending}
              onClick={() => onPick(null)}
            >
              {isPending ? "…" : "Auswahl entfernen"}
            </button>
          ) : (
            <button
              type="button"
              className="btn primary"
              disabled={isPending}
              onClick={() => onPick(k.artikelCode)}
            >
              {isPending ? "…" : `Übernehmen: ${k.artikelCode}`}
            </button>
          )}
          <button type="button" className="btn ghost" onClick={onClose}>
            Schliessen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ScoreBand({
  label,
  cls,
  desc,
  active,
}: {
  label: string;
  cls: "s-ok" | "s-warn" | "s-bad";
  desc: string;
  active: boolean;
}) {
  return (
    <div className={`score-band ${active ? "is-active" : ""}`}>
      <span className={`cc-score-pill ${cls}`}>{label}</span>
      <span className="score-band-desc">{desc}</span>
    </div>
  );
}

function SchnellkalkSummaryCard({
  schnellkalk,
  onEdit,
  onRemove,
  isPending,
}: {
  schnellkalk: Schnellkalkulation;
  onEdit: () => void;
  onRemove: () => void;
  isPending: boolean;
}) {
  // Aggregat pro Kostenart fuer die Pill-Anzeige.
  const summen = useMemo(() => {
    const acc: Record<"lohn" | "material" | "geraet" | "sonstige" | "fremd", number> = {
      lohn: 0,
      material: 0,
      geraet: 0,
      sonstige: 0,
      fremd: 0,
    };
    let total = 0;
    for (const z of schnellkalk.zeilen) {
      const zp = Math.round(z.einzelpreis * z.menge * 100) / 100;
      acc[z.kostenart] = Math.round((acc[z.kostenart] + zp) * 100) / 100;
      total = Math.round((total + zp) * 100) / 100;
    }
    return { perKostenart: acc, total };
  }, [schnellkalk]);

  type SummaryKey = "lohn" | "material" | "geraet" | "sonstige" | "fremd";
  const ordered: Array<{ key: SummaryKey; label: string; tone: string }> = [
    { key: "lohn", label: "Lohn", tone: "var(--acc-hot)" },
    { key: "material", label: "Material", tone: "var(--ok)" },
    { key: "geraet", label: "Geraet", tone: "var(--acc)" },
    { key: "sonstige", label: "Sonstige", tone: "var(--warn)" },
    { key: "fremd", label: "Fremd", tone: "var(--fg-dim)" },
  ];

  return (
    <div className="candidate cand-pick" style={{ display: "block" }}>
      <div className="cand-head">
        <span className="uppercase-mini" style={{ color: "var(--fg-dim)" }}>
          SCHNELLKALKULATION
        </span>
        <span className="pill ok" style={{ marginLeft: "auto" }}>
          <span className="dot" />
          {schnellkalk.zeilen.length} Zeile
          {schnellkalk.zeilen.length === 1 ? "" : "n"} ·{" "}
          {schnellkalk.mengenbezug === "pauschal" ? "pauschal" : "pro Einheit"}
        </span>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
        {ordered.map((k) => {
          const val = summen.perKostenart[k.key];
          if (val === 0) return null;
          return (
            <span
              key={k.key}
              className="pill"
              style={{
                borderColor: k.tone,
                color: k.tone,
                padding: "2px 10px",
                fontSize: 11,
              }}
            >
              <span style={{ fontWeight: 600, marginRight: 6 }}>{k.label}</span>
              <span className="mono">
                {val.toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                €
              </span>
            </span>
          );
        })}
      </div>
      {schnellkalk.notiz && (
        <div
          className="dim"
          style={{ marginTop: 10, fontSize: 12, fontStyle: "italic" }}
        >
          {schnellkalk.notiz}
        </div>
      )}
      <div
        style={{
          marginTop: 12,
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <span className="mono" style={{ color: "var(--acc-hot)" }}>
          Σ ={" "}
          {summen.total.toLocaleString("de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{" "}
          €
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn ghost sm"
          onClick={onRemove}
          disabled={isPending}
        >
          {isPending ? "…" : "Entfernen"}
        </button>
        <button
          type="button"
          className="btn sm primary"
          onClick={onEdit}
          disabled={isPending}
        >
          Bearbeiten
        </button>
      </div>
    </div>
  );
}

/** Top-Kandidat aus Match (höchste Score, status != nicht_geeignet) */
export function topCandidate(
  match: ArtikelMatchPosition
): { artikelCode: string; matchScore: number; status: string } | null {
  const sorted = [...match.matchKandidaten]
    .filter((k) => k.status !== "nicht_geeignet")
    .sort((a, b) => b.matchScore - a.matchScore);
  return sorted[0] ?? null;
}
